import { forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '../../lib/cn.js'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'children'
> {
  options: SelectOption[]
  /** Shown first and selected when the value is empty — "All", "Choose a city". */
  placeholder?: string
  invalid?: boolean
  size?: 'sm' | 'md'
}

/**
 * A choice from a known list.
 *
 * The native control on purpose. A desk is used with a keyboard all day, a native
 * select is the only one that behaves correctly on every platform without being
 * rebuilt, and nothing on these screens needs a custom row inside the list. Where
 * one eventually does — an avatar beside a name — that is a different component
 * and it should say so rather than this one growing a mode.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, invalid, size = 'md', ...props },
  ref,
) {
  return (
    <div className="relative flex items-center">
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'w-full appearance-none rounded-md border bg-surface pr-9 text-ink outline-none transition-colors',
          'focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
          'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
          size === 'sm' ? 'h-9 pl-3 text-second' : 'h-10 pl-3 text-body',
          invalid ? 'border-error' : 'border-hairline-strong',
          className,
        )}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 size-4 text-ink-muted"
        aria-hidden
      />
    </div>
  )
})
